/**
 * State-timeline plumbing, kept out of the component file so the repo's
 * fast-refresh lint rule stays happy (same reason obsChart.ts exists).
 *
 * The interesting decision lives here: what a MISSING sample means. A health
 * series that simply stops is not a healthy one, and a chart that joins across
 * the hole says it is.
 */

/** What one stretch of a band means. */
export type BandState = 'up' | 'down' | 'gap';

export interface Segment {
  state: BandState;
  /** Unix seconds, inclusive start / exclusive end. */
  from: number;
  to: number;
}

export const STATE_CLASS: Record<BandState, string> = {
  up: 'bg-success-500',
  down: 'bg-danger-500',
  gap: 'bg-gray-200',
};

export const STATE_LABEL: Record<BandState, string> = {
  up: 'up',
  down: 'down',
  gap: 'no data',
};

/**
 * Lay a series' samples on the window's grid and coalesce runs.
 *
 * Exported for the sake of being testable by eye in isolation; the gap rule is
 * the part worth stating: a sample is expected every `step`, so a jump larger
 * than 1.5 steps is a hole in the data rather than a long-held state. The
 * tolerance is there because a scrape lands a little late far more often than
 * it goes missing.
 */
export function toSegments(
  points: Array<{ t: number; v: number }>,
  start: number,
  end: number,
  step: number,
): Segment[] {
  const segs: Segment[] = [];
  const push = (state: BandState, from: number, to: number) => {
    if (to <= from) return;
    const last = segs[segs.length - 1];
    if (last && last.state === state && last.to === from) {
      last.to = to;
      return;
    }
    segs.push({ state, from, to });
  };

  const gapAfter = step * 1.5;
  let cursor = start;

  for (const p of points) {
    if (p.t > cursor + gapAfter) push('gap', cursor, p.t);
    else if (p.t > cursor) push(segs.length ? segs[segs.length - 1].state : 'gap', cursor, p.t);
    const state: BandState = Number.isFinite(p.v) && p.v >= 1 ? 'up' : 'down';
    push(state, Math.max(p.t, cursor), p.t + step);
    cursor = Math.max(cursor, p.t + step);
  }

  if (cursor < end) push('gap', cursor, end);
  if (segs.length === 0) push('gap', start, end);

  // Clamp the tail: the last sample's step can overhang the window.
  const last = segs[segs.length - 1];
  if (last.to > end) last.to = Math.max(end, last.from + 1);
  return segs;
}

/** Current state = the state of the last segment that is not a gap at the end. */
export function currentState(segs: Segment[]): BandState {
  return segs.length ? segs[segs.length - 1].state : 'gap';
}

export function fmtClock(t: number): string {
  return new Date(t * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
